import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

type SortKey = "rank" | "city" | "consultant" | "count" | "activations" | "conversionRate";
type SortDir = "asc" | "desc";

type Props = {
  leadsByConsultant: { consultant: string; count: number; city?: string }[];
  activationsByConsultant: {
    activations: number;
    reactivations: number;
    technicalConsultant: string;
    city?: string;
  }[];
};

const MEDAL_COLORS: Record<number, string> = {
  1: "text-yellow-500 font-bold",
  2: "text-gray-400 font-bold",
  3: "text-amber-600 font-bold",
};

function SortIcon({ dir }: { dir: SortDir | null }) {
  if (!dir) {
    return (
      <span className="inline-flex flex-col ml-1 opacity-30 text-[8px] leading-none">
        <span>▲</span>
        <span>▼</span>
      </span>
    );
  }
  return (
    <span className="inline-flex ml-1 text-[10px] leading-none">
      {dir === "asc" ? "▲" : "▼"}
    </span>
  );
}

export function LeadsByConsultant({
  leadsByConsultant,
  activationsByConsultant,
}: Props) {
  const [sortKey, setSortKey] = useState<SortKey>("count");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "city" || key === "consultant" ? "asc" : "desc");
    }
  };

  const getConsultantData = (consultantName: string, city?: string) => {
    const found =
      activationsByConsultant.find(
        (a) =>
          a.technicalConsultant === consultantName &&
          (city ? a.city === city : true)
      ) ??
      activationsByConsultant.find(
        (a) => a.technicalConsultant === consultantName
      );
    return {
      activations: found?.activations ?? 0,
      city: found?.city,
    };
  };

  const rows = [...leadsByConsultant]
    .sort((a, b) => b.count - a.count)
    .map((item, index) => {
      const { activations, city: activationCity } = getConsultantData(
        item.consultant,
        item.city
      );
      const city = item.city ?? activationCity;
      const conversionRate =
        item.count > 0 ? (activations / item.count) * 100 : 0;
      return { rank: index + 1, ...item, activations, city, conversionRate };
    })
    .sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      if (sortKey === "city") {
        return dir * (a.city ?? "").localeCompare(b.city ?? "");
      }
      if (sortKey === "consultant") {
        return dir * a.consultant.localeCompare(b.consultant);
      }
      const aVal = a[sortKey] as number;
      const bVal = b[sortKey] as number;
      return dir * (aVal - bVal);
    });

  const thClass =
    "py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider select-none cursor-pointer hover:text-gray-800 hover:bg-gray-100 transition-colors whitespace-nowrap";

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="font-bold">Ranking de Consultores</CardTitle>
      </CardHeader>
      <CardContent className="p-0 pb-2">
        {rows.length > 0 ? (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th
                    className={`${thClass} text-center w-12`}
                    onClick={() => handleSort("rank")}
                  >
                    #
                    <SortIcon dir={sortKey === "rank" ? sortDir : null} />
                  </th>
                  <th
                    className={`${thClass} text-left`}
                    onClick={() => handleSort("city")}
                  >
                    Cidade
                    <SortIcon dir={sortKey === "city" ? sortDir : null} />
                  </th>
                  <th
                    className={`${thClass} text-left`}
                    onClick={() => handleSort("consultant")}
                  >
                    Consultor
                    <SortIcon dir={sortKey === "consultant" ? sortDir : null} />
                  </th>
                  <th
                    className={`${thClass} text-right`}
                    onClick={() => handleSort("count")}
                  >
                    Leads
                    <SortIcon dir={sortKey === "count" ? sortDir : null} />
                  </th>
                  <th
                    className={`${thClass} text-right`}
                    onClick={() => handleSort("activations")}
                  >
                    Ativações
                    <SortIcon dir={sortKey === "activations" ? sortDir : null} />
                  </th>
                  <th
                    className={`${thClass} text-right`}
                    onClick={() => handleSort("conversionRate")}
                  >
                    Conversão
                    <SortIcon dir={sortKey === "conversionRate" ? sortDir : null} />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((item) => (
                  <tr
                    key={`${item.consultant}-${item.city ?? "sem-cidade"}-${item.rank}`}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-3 px-4 text-center">
                      <span
                        className={`text-sm ${
                          MEDAL_COLORS[item.rank] ?? "text-gray-400 font-medium"
                        }`}
                      >
                        {item.rank}º
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-500 text-sm">
                      {item.city ?? <span className="text-gray-300">—</span>}
                    </td>
                    <td className="py-3 px-4 font-medium text-gray-800">
                      {item.consultant}
                    </td>
                    <td className="py-3 px-4 text-right font-semibold text-gray-800">
                      {item.count}
                    </td>
                    <td className="py-3 px-4 text-right font-semibold text-gray-800">
                      {item.activations}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span
                        className={`font-semibold ${
                          item.conversionRate >= 10
                            ? "text-green-600"
                            : item.conversionRate >= 5
                            ? "text-yellow-600"
                            : "text-red-500"
                        }`}
                      >
                        {item.conversionRate.toFixed(2)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center text-gray-500 py-8">
            Nenhum consultor encontrado.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
